import React, { ErrorInfo, ReactNode } from "react";
import { captureException } from "@sentry/react";
import { initializeSentry } from "../../../constant/sentryConfiguration";
import { isChunkLoadError } from "../../../utils/lazyWithRetry";
import { colors, sp, fsPx, fw, lh } from "../../../styles/design-tokens";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: unknown;
}

/**
 * Last line of defence against a blank document.
 *
 * React 18 unmounts the whole tree when a render error goes unhandled, and with
 * no boundary above the app the result was a white page with nothing on it —
 * what a stale code-split chunk produced on /paymentsuccess after a deploy.
 * Whatever the cause, an error should leave something on screen that says what
 * happened and offers a way out.
 *
 * Mounted outermost in `index.tsx`, above the router and every provider, so it
 * cannot use them (no Typography, no AppButton, no context). Plain elements and
 * inline styles from the design tokens; the button takes the brand colour from
 * the CSS variable that `applyBrandingAtBoot` sets before the first render.
 */
class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // An error caught here never reaches window.onerror, so Sentry's global
    // handler would not see it — report it explicitly, with the component stack.
    // Sentry starts at idle time, so a crash during the first render can land
    // before it; wait for (or start) the shared init so the event is not dropped.
    initializeSentry()
      .catch(() => {})
      .then(() => captureException(error, { contexts: { react: { componentStack: info?.componentStack } } }));
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // A stale chunk is not a bug the user can act on: the app is simply older
    // than the server. Say that, rather than showing an error for a fixed problem.
    const isStaleBuild = isChunkLoadError(error);

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          fontFamily: "inherit",
          background: colors.background.main,
        }}
      >
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <h1 style={{ fontSize: fsPx(20), fontWeight: fw(600), margin: sp(0, 0, 8), color: colors.text.primary }}>
            {isStaleBuild ? "A newer version is available" : "Something went wrong"}
          </h1>
          <p style={{ fontSize: fsPx(14), lineHeight: lh(1.5), margin: sp(0, 0, 20), color: colors.text.muted }}>
            {isStaleBuild
              ? "This tab was open while the app was updated. Reload to pick up the new version."
              : "This page could not be displayed. Reloading usually fixes it."}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: sp(10, 24),
              fontSize: fsPx(14),
              fontWeight: fw(600),
              color: colors.neutral.white,
              background: "var(--primary-main)",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
